import type { RosterStatus } from '@ukinfo/shared';
import { ATTENDANCE_BAND_LABELS, ROSTER_STATUS } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatShort, relativeDays } from '../lib/format';
import {
  iso,
  localDateTime,
  plusMinutes,
  today,
  ageClassFor,
  ageOn,
  formatDay,
  type AttendanceSummary,
  type GeneratedPersonalAccessLink,
  type PersonalAccessLink,
  type Match,
  ATTENDANCE_BAND_STYLE,
  ROSTER_STATUS_LABELS,
  type RosterRow,
  type Season,
  type Session,
  type Team,
} from '../lib/sports';
import {
  absageGrundAnhaengen,
  absageGrundEntfernen,
  absageGrundLesen,
  anwesenheitLabel,
  anwesenheitOffen,
  invalidateSports,
  istKommend,
} from '../lib/sports-termine';
import { AttendanceDialog, type AttendanceSource } from '../components/AttendanceDialog';
import { AbsencesCard } from '../components/AbsencesCard';
import { RegistrationLinkCard } from '../components/RegistrationLinkCard';
import { useConfirm } from '../components/ConfirmDialog';
import { ResponseTally, type ResponseSummary } from '../components/ResponseTally';
import { MehrMenu } from '../components/MehrMenu';
import { ShareActions, ShareDialog, type ShareTarget } from '../components/ShareLink';
import { TeamDivisionCard, TeamSettingsCard } from '../components/TeamSettingsCard';
import { TransferDialog, TransferHistory } from '../components/TransferDialog';
import { PageHeader } from '../components/PageHeader';
import { PersonSelect } from '../components/PersonPicker';
import { SessionPlanButton } from '../components/training/SessionPlanSection';
import { TagList, TagPicker } from '../components/TagPicker';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  SectionTitle,
  Select,
  Tabs,
  cn,
  useToast,
} from '../components/ui';

const PRO_SEITE = 20;
const REITER = ['termine', 'kader', 'links', 'auswertungen', 'einstellungen'] as const;
/** Alte Adressen weiterleiten: Trainings und Spiele stehen jetzt zusammen. */
const ALTE_REITER: Record<string, Reiter> = { trainings: 'termine', spiele: 'termine' };
type Reiter = (typeof REITER)[number];

/**
 * Eine Mannschaft mit allem, was zu ihr gehoert.
 *
 * Reiter statt Untereinander: Trainings, Spiele, Kader und Auswertungen
 * beantworten verschiedene Fragen und werden selten gleichzeitig gebraucht.
 * Reiter und Saison stehen in der Adresse - ein Link auf die Auswertung
 * einer bestimmten Saison bleibt damit teilbar.
 */
/**
 * Anwesenheit: die Hauptaufgabe der Trainerin am Termin - deshalb farbig und
 * mit Symbol, klar getrennt von der eigenen Zu- oder Absage.
 */
function AttendanceButton({ onClick, label = 'Anwesenheit' }: { onClick: () => void; label?: string }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="6" y="4" width="12" height="17" rx="2" />
        <path d="M9 4.5V3.5a1 1 0 011-1h4a1 1 0 011 1v1M9 11l2 2 4-4M9 17h6" />
      </svg>
      {label}
    </button>
  );
}

export function TeamPage(): JSX.Element {
  const { teamId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { can, user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();

  const reiterParam = params.get('reiter');
  const reiter: Reiter = REITER.includes(reiterParam as Reiter)
    ? (reiterParam as Reiter)
    : ALTE_REITER[reiterParam ?? ''] ?? 'termine';

  const [attendance, setAttendance] = useState<{ source: AttendanceSource; title: string } | null>(null);
  const [spielOrt, setSpielOrt] = useState<'alle' | 'heim' | 'auswaerts'>('alle');
  const [matchDialog, setMatchDialog] = useState(false);
  const [matchOpponent, setMatchOpponent] = useState('');
  const [matchStart, setMatchStart] = useState(localDateTime());
  const [matchLocation, setMatchLocation] = useState('');
  const [matchCompetition, setMatchCompetition] = useState('');
  const [matchIsHome, setMatchIsHome] = useState(true);
  const [spielWeg, setSpielWeg] = useState<Match | null>(null);

  const seasons = useQuery({
    queryKey: ['sports-seasons'],
    queryFn: () => api.get<Season[]>('/api/sports/seasons'),
  });
  const seasonId = params.get('saison') ?? seasons.data?.[0]?.id ?? '';
  const teams = useQuery({
    queryKey: ['sports-teams', 'alle', seasonId],
    queryFn: () => api.get<Team[]>('/api/sports/teams', { seasonId }),
    enabled: Boolean(seasonId),
  });

  const team = teams.data?.find((t) => t.id === teamId);
  const bereit = Boolean(teamId && seasonId);

  const setParam = (schluessel: string, wert: string): void => {
    const naechste = new URLSearchParams(params);
    naechste.set(schluessel, wert);
    setParams(naechste, { replace: true });
  };

  const sessions = useQuery({
    queryKey: ['sports-sessions', teamId, seasonId],
    queryFn: () => api.get<Session[]>(`/api/sports/teams/${teamId}/sessions`, { seasonId }),
    enabled: bereit,
  });
  // Unter 'sports-sessions', damit jede Rueckmeldung die Zahlen gleich mit auffrischt.
  const summaries = useQuery({
    queryKey: ['sports-sessions', teamId, seasonId, 'summary'],
    queryFn: () => api.get<ResponseSummary[]>(`/api/sports/teams/${teamId}/response-summary`, { seasonId }),
    enabled: bereit,
  });
  const summaryBySession = new Map((summaries.data ?? []).map((row) => [row.session_id, row]));
  const summaryByMatch = new Map((summaries.data ?? []).filter((row) => row.match_id).map((row) => [row.match_id as string, row]));
  const roster = useQuery({
    queryKey: ['sports-roster', teamId, seasonId],
    queryFn: () => api.get<RosterRow[]>(`/api/sports/teams/${teamId}/roster`, { seasonId }),
    enabled: bereit && can('persons', 'read'),
  });
  const matches = useQuery({
    queryKey: ['sports-matches', teamId, team?.organisation_unit_id],
    queryFn: () =>
      api.get<Match[]>('/api/matches', {
        organisationUnitId: team?.organisation_unit_id,
        teamId,
        includeChildren: false,
      }),
    enabled: Boolean(team),
  });

  const createMatch = useMutation({
    mutationFn: () =>
      api.post('/api/matches', {
        organisationUnitId: team!.organisation_unit_id,
        teamId,
        homeTeam: matchIsHome ? team!.name : matchOpponent.trim(),
        awayTeam: matchIsHome ? matchOpponent.trim() : team!.name,
        startAt: iso(matchStart),
        location: matchLocation,
        competition: matchCompetition,
        isHome: matchIsHome,
      }),
    onSuccess: async () => {
      await invalidateSports(qc);
      setMatchDialog(false);
      setMatchOpponent('');
      toast.show('Spiel angelegt');
    },
    onError: (error) => toast.show(error instanceof ApiError ? error.message : 'Spiel konnte nicht angelegt werden', 'error'),
  });

  /**
   * Ein Spiel loeschen - Termin und Spiel gemeinsam. Verbandsspiele weist der
   * Server ab; sie kommen beim naechsten Abgleich ohnehin wieder.
   */
  const spielLoeschen = useMutation({
    mutationFn: (id: string) => api.del(`/api/matches/${id}`),
    onSuccess: async () => {
      await invalidateSports(qc);
      setSpielWeg(null);
      toast.show('Spiel gelöscht');
    },
    onError: (error) => toast.show(error instanceof ApiError ? error.message : 'Spiel konnte nicht gelöscht werden', 'error'),
  });

  // Aus dem Kalender kommt die Sitzungs-ID des Termins mit (auch bei Spielen):
  // dann oeffnet sich die Anwesenheit sofort, statt sie in der Liste suchen
  // zu lassen. Bei Spielen fuehrt die Antwort der Rueckmeldungen von der
  // Sitzung zum Spiel.
  const anwesenheitParam = params.get('anwesenheit');
  const anwesenheitLaedt = sessions.isPending || matches.isPending || summaries.isPending;
  const anwesenheitFehler = Boolean(sessions.error || matches.error || summaries.error);
  useEffect(() => {
    if (!anwesenheitParam || anwesenheitLaedt || anwesenheitFehler) return;
    const termin = (sessions.data ?? []).find((s) => s.id === anwesenheitParam);
    const spielId = (summaries.data ?? []).find((row) => row.session_id === anwesenheitParam && row.match_id)?.match_id;
    const spiel = termin ? undefined
      : (matches.data ?? []).find((m) => m.id === spielId || m.id === anwesenheitParam);
    if (termin || spiel) {
      setAttendance(termin
        ? { source: { kind: 'session', sessionId: termin.id }, title: `Anwesenheit · ${termin.title}` }
        : { source: { kind: 'match', teamId, matchId: spiel!.id }, title: `Anwesenheit · ${spiel!.homeTeam}${spiel!.awayTeam ? ` - ${spiel!.awayTeam}` : ''}` });
    } else {
      toast.show('Termin nicht gefunden – liegt er in einer anderen Saison?', 'error');
    }
    setParams((vorher) => {
      const naechste = new URLSearchParams(vorher);
      naechste.delete('anwesenheit');
      return naechste;
    }, { replace: true });
  }, [anwesenheitParam, anwesenheitLaedt, anwesenheitFehler, sessions.data, matches.data, summaries.data, teamId, setParams, toast]);

  const mayTrain = team?.capabilities.canEditEvent ?? false;

  if (teams.isLoading || seasons.isLoading) return <LoadingBlock />;
  // Sonst meldet ein Netzfehler "Mannschaft nicht gefunden" - und man sucht
  // den Fehler an der falschen Stelle.
  if (teams.error) return <ErrorBlock error={teams.error} />;
  if (!team) {
    return (
      <EmptyState
        title="Mannschaft nicht gefunden"
        description="Sie ist gelöscht worden oder liegt außerhalb deines Zugriffsbereichs."
      />
    );
  }

  // Abgeschlossen: gespielt, abgesagt oder seit drei Stunden vorbei.
  const abgeschlossen = (m: Match): boolean => m.status === 'PLAYED' || m.status === 'CANCELLED' || Date.parse(m.startAt) < Date.now() - 3 * 3600_000;
  const spieleNachOrt = (matches.data ?? []).filter((m) => spielOrt === 'alle' || (spielOrt === 'heim') === m.isHome);
  const spieleKommend = spieleNachOrt.filter((m) => !abgeschlossen(m));

  // Kommend ist ein Termin bis zu seinem Ende (plus Kulanz). Vergangene
  // Trainings ohne bestaetigte Anwesenheit mahnt die Liste oben an - aber nur
  // dort, wo die Person die Anwesenheit auch fuehren darf.
  const jetzt = Date.now();
  const alleTermine = sessions.data ?? [];
  const kommende = alleTermine.filter((s) => istKommend(s, jetzt));
  const offene = team.capabilities.canRecordAttendance ? alleTermine.filter((s) => anwesenheitOffen(s, jetzt)) : [];
  const offeneIds = new Set(offene.map((s) => s.id));
  const vergangene = alleTermine
    .filter((s) => !istKommend(s, jetzt) && !offeneIds.has(s.id))
    .reverse();
  // Ob die Person selbst zum Termin gehoert, liefert die Terminliste nicht;
  // der Kader ist der beste Anhaltspunkt. Ist er nicht lesbar, bleiben die Knoepfe.
  const selbstImKader = roster.data ? roster.data.some((r) => r.person_id === user?.personId) : !can('persons', 'read');
  const termineFehler = [sessions, matches, summaries].filter((q) => q.error);

  return (
    <div className="space-y-4">
      <div>
        <Link to="/sport" className="text-sm text-brand-700 hover:underline">
          ← Alle Mannschaften
        </Link>
      </div>

      <PageHeader
        title={team.name}
        description={[
          team.unit_name && team.unit_name !== team.name ? team.unit_name : null,
          team.nextSession ? `Als Nächstes: ${team.nextSession.title} · ${formatShort(team.nextSession.startAt)}` : null,
        ].filter(Boolean).join(' · ') || undefined}
        scoped={false}
        actions={
          (seasons.data?.length ?? 0) > 1 ? (
            <Select
              aria-label="Saison"
              value={seasonId}
              onChange={(e) => setParam('saison', e.target.value)}
            >
              {seasons.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          ) : undefined
        }
      />

      <Tabs
        tabs={[
          { id: 'termine', label: 'Termine', badge: kommende.length + offene.length + spieleKommend.length },
          { id: 'kader', label: 'Kader', badge: roster.data?.filter((r) => r.status !== 'OCCASIONAL').length },
          ...(team.canManage ? [{ id: 'links', label: 'Persönliche Links' }] : []),
          { id: 'auswertungen', label: 'Auswertungen' },
          ...(team.capabilities.canRespondForOthers ? [{ id: 'einstellungen', label: 'Einstellungen' }] : []),
        ]}
        active={reiter}
        onChange={(id) => setParam('reiter', id)}
      />

      {reiter === 'termine' && (
        <TermineTab
          teamId={teamId}
          seasonId={seasonId}
          kommende={kommende}
          offene={offene}
          vergangene={vergangene}
          selbstImKader={selbstImKader}
          isLoading={sessions.isLoading || matches.isLoading}
          ladeFehler={termineFehler.length > 0
            ? { error: termineFehler[0]!.error, erneut: () => termineFehler.forEach((q) => void q.refetch()) }
            : null}
          mayTrain={mayTrain}
          canManage={team.canManage}
          onAttendance={setAttendance}
          team={team}
          otherTeams={(teams.data ?? []).filter((t) => t.id !== team.id)}
          summaries={summaryBySession}
          summaryByMatch={summaryByMatch}
          spiele={spieleNachOrt}
          spielOrt={spielOrt}
          onSpielOrt={setSpielOrt}
          mayCreateMatch={can('matches', 'create')}
          onCreateMatch={() => {
            setMatchStart(localDateTime());
            setMatchIsHome(true);
            setMatchDialog(true);
          }}
          onDeleteMatch={setSpielWeg}
          abgeschlossen={abgeschlossen}
        />
      )}

      {reiter === 'kader' && (
        <div className="space-y-3">
          <KaderTab teamId={teamId} seasonId={seasonId} rows={roster.data} team={team} season={seasons.data?.find((s) => s.id === seasonId)} otherTeams={(teams.data ?? []).filter((t) => t.id !== team.id)} />
          {team.capabilities.canReadParticipants && (
            <AbsencesCard teamId={teamId} seasonId={seasonId} roster={roster.data ?? []} />
          )}
        </div>
      )}

      {reiter === 'links' && team.canManage && <EinladungCard key={`${teamId}:${seasonId}`} teamId={teamId} seasonId={seasonId} />}

      {reiter === 'einstellungen' && team.capabilities.canRespondForOthers && (
        <div className="space-y-3">
          {can('persons', 'update') && <TeamDivisionCard team={team} seasonId={seasonId} />}
          <TeamSettingsCard teamId={teamId} youthAgeClass={team.category === 'YOUTH' ? team.ageClass ?? null : null} />
        </div>
      )}

      {reiter === 'auswertungen' && (
        <AuswertungenTab
          key={seasonId}
          teamId={teamId}
          seasonId={seasonId}
          season={seasons.data?.find((s) => s.id === seasonId)}
        />
      )}

      {attendance && (
        <AttendanceDialog
          open
          onClose={() => {
            setAttendance(null);
            void invalidateSports(qc);
          }}
          title={attendance.title}
          source={attendance.source}
        />
      )}

      <Dialog
        open={matchDialog}
        onClose={() => setMatchDialog(false)}
        title="Spiel anlegen"
        footer={<>
          <Button variant="secondary" onClick={() => setMatchDialog(false)}>Abbrechen</Button>
          <Button
            loading={createMatch.isPending}
            disabled={!matchOpponent.trim() || !matchStart}
            onClick={() => createMatch.mutate()}
          >
            Anlegen
          </Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Mannschaft: <strong>{team.name}</strong></p>
          <Field label="Gegner" required>
            {(id) => <Input id={id} value={matchOpponent} onChange={(event) => setMatchOpponent(event.target.value)} placeholder="z. B. FC Musterstadt" autoFocus />}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Termin" required>
              {(id) => <Input id={id} type="datetime-local" value={matchStart} onChange={(event) => setMatchStart(event.target.value)} />}
            </Field>
            <Field label="Austragung">
              {(id) => <Select id={id} value={matchIsHome ? 'home' : 'away'} onChange={(event) => setMatchIsHome(event.target.value === 'home')}>
                <option value="home">Heimspiel</option>
                <option value="away">Auswärtsspiel</option>
              </Select>}
            </Field>
          </div>
          <Field label="Ort">
            {(id) => <Input id={id} value={matchLocation} onChange={(event) => setMatchLocation(event.target.value)} placeholder="Sportplatz / Halle" />}
          </Field>
          <Field label="Wettbewerb">
            {(id) => <Input id={id} value={matchCompetition} onChange={(event) => setMatchCompetition(event.target.value)} placeholder="z. B. Meisterschaft" />}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={spielWeg !== null}
        onClose={() => setSpielWeg(null)}
        title="Spiel löschen"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSpielWeg(null)}>Abbrechen</Button>
            <Button variant="danger" loading={spielLoeschen.isPending} onClick={() => spielWeg && spielLoeschen.mutate(spielWeg.id)}>
              Spiel löschen
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          {spielWeg ? `${spielWeg.homeTeam}${spielWeg.awayTeam ? ` - ${spielWeg.awayTeam}` : ''} am ${formatShort(spielWeg.startAt)}` : ''} wird
          samt Termin, Rückmeldungen und Aufstellung gelöscht. Fällt das Spiel nur aus, ist es besser, es auf
          „Abgesagt" zu setzen – dann bleibt es sichtbar.
        </p>
      </Dialog>

      {/* Ein Fehler in einer Nebenabfrage soll die Seite nicht leeren. */}
      {roster.error && can('persons', 'read') && (
        <p className="text-sm text-rose-700">Der Kader konnte nicht geladen werden.</p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Termine */

function TermineTab({
  teamId,
  seasonId,
  kommende,
  offene,
  vergangene,
  selbstImKader,
  isLoading,
  ladeFehler,
  mayTrain,
  canManage,
  onAttendance,
  team,
  otherTeams,
  summaries,
  summaryByMatch,
  spiele,
  spielOrt,
  onSpielOrt,
  mayCreateMatch,
  onCreateMatch,
  onDeleteMatch,
  abgeschlossen,
}: {
  team: Team;
  otherTeams: Team[];
  summaries: Map<string, ResponseSummary>;
  summaryByMatch: Map<string, ResponseSummary>;
  spiele: Match[];
  spielOrt: 'alle' | 'heim' | 'auswaerts';
  onSpielOrt: (wert: 'alle' | 'heim' | 'auswaerts') => void;
  mayCreateMatch: boolean;
  onCreateMatch: () => void;
  onDeleteMatch: (spiel: Match) => void;
  abgeschlossen: (spiel: Match) => boolean;
  teamId: string;
  seasonId: string;
  kommende: Session[];
  /** Vergangene Termine, deren Anwesenheit noch nicht bestätigt ist. */
  offene: Session[];
  vergangene: Session[];
  /** Ob die angemeldete Person im Kader steht und damit selbst antworten kann. */
  selbstImKader: boolean;
  isLoading: boolean;
  ladeFehler: { error: unknown; erneut: () => void } | null;
  mayTrain: boolean;
  canManage: boolean;
  onAttendance: (wert: { source: AttendanceSource; title: string }) => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [gaeste, setGaeste] = useState<string[]>([]);
  // Wie beim Dazuholen: Erwachsene desselben Bereichs, im Nachwuchs andere Nachwuchsmannschaften.
  const einladbar = otherTeams.filter((t) => team.category === 'YOUTH'
    ? t.category === 'YOUTH' && (t.division === 'MIXED' || team.division === 'MIXED' || t.division === team.division)
    : t.category !== 'YOUTH' && t.division === team.division);
  const [dialog, setDialog] = useState(false);
  const [art2, setArt2] = useState<'alle' | 'TRAINING' | 'WORK' | 'MATCH'>('alle');
  const [zeit, setZeit] = useState<'kommend' | 'vergangen' | 'alle'>('kommend');
  const [seite, setSeite] = useState(1);
  const [art, setArt] = useState<'TRAINING' | 'WORK'>('TRAINING');
  const [title, setTitle] = useState('Training');
  const [location, setLocation] = useState('');
  const [start, setStart] = useState(localDateTime());
  const [ende, setEnde] = useState(() => plusMinutes(localDateTime(), 90));
  const [serie, setSerie] = useState(false);
  const [von, setVon] = useState(() => new Date().toISOString().slice(0, 10));
  const [bis, setBis] = useState(() =>
    new Date(Date.now() + 84 * 86_400_000).toISOString().slice(0, 10),
  );
  const [tage, setTage] = useState<number[]>([2, 4]);
  const [vonZeit, setVonZeit] = useState('19:00');
  const [bisZeit, setBisZeit] = useState('20:30');
  const [absage, setAbsage] = useState<Session | null>(null);
  const [grund, setGrund] = useState('');
  const [loeschen, setLoeschen] = useState<Session | null>(null);
  const [loeschUmfang, setLoeschUmfang] = useState<'single' | 'following'>('single');
  /** Arbeitseinsaetze heissen nie "Training" - weder im Dialog noch im Toast. */
  const bezeichnung = (termin: Session | null): string => (termin?.kind === 'WORK' ? 'Arbeitseinsatz' : 'Training');
  const jetzt = Date.now();

  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const createSession = useMutation({
    mutationFn: () =>
      api.post('/api/sports/sessions', {
        teamId,
        seasonId,
        title,
        kind: art,
        startAt: iso(start),
        endAt: iso(ende),
        location,
        description: '',
        ...(gaeste.length ? { guestTeamIds: gaeste } : {}),
      }),
    onSuccess: async () => {
      await invalidateSports(qc);
      setDialog(false);
      setGaeste([]);
      toast.show(art === 'WORK' ? 'Arbeitseinsatz angelegt' : 'Training angelegt');
    },
    onError: fehler(art === 'WORK' ? 'Arbeitseinsatz konnte nicht angelegt werden' : 'Training konnte nicht angelegt werden'),
  });

  const createSeries = useMutation({
    mutationFn: () =>
      api.post<{ created: number }>('/api/sports/session-series', {
        teamId,
        seasonId,
        title,
        kind: art,
        location,
        description: '',
        fromDate: von,
        toDate: bis,
        weekdays: tage,
        startTime: vonZeit,
        endTime: bisZeit,
        ...(gaeste.length ? { guestTeamIds: gaeste } : {}),
      }),
    onSuccess: async (ergebnis) => {
      await invalidateSports(qc);
      setDialog(false);
      toast.show(`${ergebnis.created} ${art === 'WORK' ? (ergebnis.created === 1 ? 'Arbeitseinsatz' : 'Arbeitseinsätze') : (ergebnis.created === 1 ? 'Training' : 'Trainings')} angelegt`);
    },
    onError: fehler(art === 'WORK' ? 'Serie der Arbeitseinsätze konnte nicht angelegt werden' : 'Trainingsserie konnte nicht angelegt werden'),
  });

  const respond = useMutation({
    mutationFn: (input: { id: string; rsvp: 'YES' | 'NO' }) =>
      api.put(`/api/sports/sessions/${input.id}/response`, { rsvp: input.rsvp }),
    onSuccess: async () => {
      await invalidateSports(qc);
      toast.show('Rückmeldung gespeichert');
    },
    onError: fehler('Rückmeldung konnte nicht gespeichert werden'),
  });

  /**
   * Ein ganzes Training absagen - etwa bei gesperrtem Platz.
   *
   * Der Grund steht als eigener, markierter Absatz in der Beschreibung des
   * Termins (eine vorhandene Beschreibung bleibt erhalten) und verschwindet
   * mit der Absage wieder: eine stehengebliebene Begruendung waere schlimmer
   * als keine.
   */
  const absagen = useMutation({
    mutationFn: (input: { session: Session; grund: string }) =>
      api.patch(`/api/sports/sessions/${input.session.id}`, {
        status: 'CANCELLED',
        ...(input.grund.trim() ? { description: absageGrundAnhaengen(input.session.description, input.grund) } : {}),
      }),
    onSuccess: async (_daten, input) => {
      await invalidateSports(qc);
      setAbsage(null);
      setGrund('');
      toast.show(`${bezeichnung(input.session)} abgesagt`);
    },
    onError: (error, input) => fehler(`${bezeichnung(input.session)} konnte nicht abgesagt werden`)(error),
  });

  const reaktivieren = useMutation({
    mutationFn: (session: Session) => {
      // Nur den beim Absagen angehaengten Grund entfernen; die Beschreibung
      // des Termins bleibt, wie sie ist. Die Antworten behaelt der Server.
      const ohneGrund = absageGrundEntfernen(session.description);
      return api.patch(`/api/sports/sessions/${session.id}`, {
        status: 'OPEN',
        ...(ohneGrund.geaendert ? { description: ohneGrund.text } : {}),
      });
    },
    onSuccess: async () => {
      await invalidateSports(qc);
      toast.show('Absage aufgehoben');
    },
    onError: fehler('Absage konnte nicht aufgehoben werden'),
  });

  /**
   * Trainings loeschen - fuer Fehlgriffe. Die Vorschau zaehlt vorab, was
   * wegfaellt; Trainings mit erfasster Anwesenheit laesst der Server stehen.
   */
  const loeschVorschau = useQuery({
    queryKey: ['sports-session-delete', loeschen?.id, loeschUmfang],
    queryFn: () =>
      api.post<{ deleted: number; skipped: number }>(`/api/sports/sessions/${loeschen!.id}/delete`, {
        scope: loeschUmfang,
        dryRun: true,
      }),
    enabled: loeschen !== null,
    retry: false,
    gcTime: 0,
  });

  const loeschMutation = useMutation({
    mutationFn: (input: { id: string; scope: 'single' | 'following' }) =>
      api.post<{ deleted: number; skipped: number }>(`/api/sports/sessions/${input.id}/delete`, { scope: input.scope }),
    onSuccess: async (result) => {
      const arbeit = loeschen?.kind === 'WORK';
      await invalidateSports(qc);
      setLoeschen(null);
      toast.show(result.deleted === 1
        ? `${arbeit ? 'Arbeitseinsatz' : 'Training'} gelöscht`
        : `${result.deleted} ${arbeit ? 'Arbeitseinsätze' : 'Trainings'} gelöscht`);
    },
    onError: fehler(loeschen?.kind === 'WORK' ? 'Arbeitseinsatz konnte nicht gelöscht werden' : 'Training konnte nicht gelöscht werden'),
  });

  const zeile = (s: Session, mitRueckmeldung: boolean, anwesenheitOffen = false): JSX.Element => {
    const abgesagt = s.status === 'CANCELLED';
    return (
      <li
        key={s.id}
        className={cn('rounded-lg px-3 py-2', abgesagt ? 'bg-rose-50/60' : 'bg-slate-50')}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span className="min-w-0">
            {/* Der Server stellt "Abgesagt: " voran - die Marke daneben sagt
                dasselbe schon deutlicher. */}
            {abgesagt ? (
              <span className="block font-medium text-slate-500 line-through">{s.title.replace(/^Abgesagt: /, '')}</span>
            ) : (
              <button
                type="button"
                title="Anwesenheit öffnen"
                onClick={() => onAttendance({ source: { kind: 'session', sessionId: s.id }, title: `Anwesenheit · ${s.title}` })}
                className="block text-left font-medium text-slate-900 hover:text-brand-700 hover:underline"
              >
                {s.title}
              </button>
            )}
            <span className="block text-xs text-slate-500">
              {formatShort(s.start_at)} · {s.location || 'kein Ort'}
            </span>
            {!abgesagt && <ResponseTally summary={summaries.get(s.id)} />}
            {s.guest_of && <span className="block text-xs text-brand-700">Gemeinsames Training der {s.guest_of}</span>}
            {!s.guest_of && s.guest_teams && s.guest_teams.length > 0 && (
              <span className="block text-xs text-brand-700">Gemeinsam mit {s.guest_teams.join(', ')}</span>
            )}
            {abgesagt && s.description && (
              <span className="mt-0.5 block text-xs text-rose-700">Grund: {absageGrundLesen(s.description)}</span>
            )}
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {abgesagt && <Badge tone="danger">Abgesagt</Badge>}
            {s.confirmed_at && <Badge tone="success">Bestätigt</Badge>}
            {anwesenheitOffen && <Badge tone="warning">Anwesenheit offen</Badge>}

            {/* Die eigene Antwort: zwei Haken wie auf der persoenlichen Seite -
                nur fuer Personen, die zum Termin gehoeren. */}
            {!abgesagt && mitRueckmeldung && selbstImKader && (
              <div className="flex overflow-hidden rounded-lg ring-1 ring-inset ring-slate-300" role="group" aria-label={`Deine Antwort · ${s.title}`}>
                <button type="button" aria-label="Zusagen" title="Zusagen" disabled={respond.isPending}
                  onClick={() => respond.mutate({ id: s.id, rsvp: 'YES' })}
                  className="flex h-9 w-9 items-center justify-center bg-white text-slate-500 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-60">
                  <span aria-hidden="true">✓</span>
                </button>
                <button type="button" aria-label="Nicht dabei" title="Nicht dabei" disabled={respond.isPending}
                  onClick={() => respond.mutate({ id: s.id, rsvp: 'NO' })}
                  className="flex h-9 w-9 items-center justify-center border-l border-slate-300 bg-white text-slate-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-60">
                  <span aria-hidden="true">✕</span>
                </button>
              </div>
            )}

            {!abgesagt && (
              <AttendanceButton
                label={anwesenheitLabel(s.start_at, jetzt)}
                onClick={() =>
                  onAttendance({
                    source: { kind: 'session', sessionId: s.id },
                    title: `Anwesenheit · ${s.title}`,
                  })
                }
              />
            )}

            {/* Trainingsplanung (Test): Einheit zum Training, nur mit Leserecht. */}
            {!abgesagt && s.kind !== 'WORK' && !s.guest_of && <SessionPlanButton session={s} teamName={team.name} />}

            {/* Seltenes steht im Menue - ob es erlaubt ist, entscheidet der Server. */}
            {canManage && !s.guest_of && (
              <MehrMenu
                label={`Weitere Aktionen · ${s.title.replace(/^Abgesagt: /, '')}`}
                eintraege={[
                  ...(abgesagt
                    ? [{ label: 'Absage aufheben', onSelect: () => reaktivieren.mutate(s) }]
                    // Bestaetigte Termine sind gespielt - die Absage waere falsch und der Server lehnt sie ab.
                    : !s.confirmed_at
                      ? [{ label: `${bezeichnung(s)} absagen`, onSelect: () => { setAbsage(s); setGrund(''); } }]
                      : []),
                  ...(!s.confirmed_at
                    ? [{ label: 'Löschen', gefaehrlich: true, onSelect: () => { setLoeschen(s); setLoeschUmfang('single'); } }]
                    : []),
                ]}
              />
            )}
          </div>
        </div>
      </li>
    );
  };

  /** Ein Spiel in derselben Liste wie Trainings. */
  const spielZeile = (m: Match): JSX.Element => (
    <li key={m.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
      <span className="min-w-0">
        <button
          type="button"
          title="Anwesenheit öffnen"
          onClick={() => onAttendance({ source: { kind: 'match', teamId, matchId: m.id }, title: `Anwesenheit · ${m.homeTeam}${m.awayTeam ? ` - ${m.awayTeam}` : ''}` })}
          className="text-left font-medium text-slate-900 hover:text-brand-700 hover:underline"
        >
          {m.awayTeam ? `${m.homeTeam} - ${m.awayTeam}` : m.homeTeam}
        </button>
        <span className="block text-xs text-slate-500">
          {formatShort(m.startAt)}
          {m.competition ? ` · ${m.competition}` : ''} · {m.isHome ? 'Heim' : 'Auswärts'}
          {m.status === 'PLAYED' && m.homeScore != null && m.awayScore != null && ` · ${m.homeScore}:${m.awayScore}`}
        </span>
        {m.status === 'CANCELLED' && <Badge tone="danger" className="mt-1">Abgesagt</Badge>}
        {m.status === 'POSTPONED' && <Badge tone="warning" className="mt-1">Verschoben</Badge>}
        {!abgeschlossen(m) && <ResponseTally summary={summaryByMatch.get(m.id)} />}
      </span>
      <span className="flex flex-wrap items-center gap-2">
        <AttendanceButton
          label={anwesenheitLabel(m.startAt, jetzt)}
          onClick={() => onAttendance({ source: { kind: 'match', teamId, matchId: m.id }, title: `Anwesenheit · ${m.homeTeam}${m.awayTeam ? ` - ${m.awayTeam}` : ''}` })}
        />
        {/* Ohne Eintraege kein Menue - ein leeres Menue waere eine Sackgasse. */}
        {canManage && (
          <MehrMenu
            label={`Weitere Aktionen · ${m.homeTeam}${m.awayTeam ? ` - ${m.awayTeam}` : ''}`}
            eintraege={[{ label: 'Löschen', gefaehrlich: true, onSelect: () => onDeleteMatch(m) }]}
          />
        )}
      </span>
    </li>
  );

  // Alles in einer Liste: Trainings, Arbeitseinsaetze und Spiele nach Datum.
  // Kommendes zuerst aufsteigend, Vergangenes absteigend - so steht das
  // Naechstliegende immer oben.
  type Eintrag = { key: string; startAt: string; art: 'TRAINING' | 'WORK' | 'MATCH'; vorbei: boolean; zeile: JSX.Element };
  const eintraege: Eintrag[] = [
    // Zuerst die vergangenen Termine ohne bestaetigte Anwesenheit: sie sind der Auftrag von gestern.
    ...offene.map((session) => ({
      key: session.id, startAt: session.start_at, art: (session.kind === 'WORK' ? 'WORK' : 'TRAINING') as 'TRAINING' | 'WORK',
      vorbei: false, zeile: zeile(session, false, true),
    })),
    ...kommende.map((session) => ({
      key: session.id, startAt: session.start_at, art: (session.kind === 'WORK' ? 'WORK' : 'TRAINING') as 'TRAINING' | 'WORK',
      vorbei: false, zeile: zeile(session, true),
    })),
    ...vergangene.map((session) => ({
      key: session.id, startAt: session.start_at, art: (session.kind === 'WORK' ? 'WORK' : 'TRAINING') as 'TRAINING' | 'WORK',
      vorbei: true, zeile: zeile(session, false),
    })),
    ...spiele.map((spiel) => ({
      key: spiel.id, startAt: spiel.startAt, art: 'MATCH' as const, vorbei: abgeschlossen(spiel), zeile: spielZeile(spiel),
    })),
  ];
  const passend = eintraege.filter((eintrag) => (art2 === 'alle' || eintrag.art === art2)
    && (zeit === 'alle' || (zeit === 'kommend') !== eintrag.vorbei));
  const sortiert = [
    ...passend.filter((eintrag) => !eintrag.vorbei).sort((a, b) => a.startAt.localeCompare(b.startAt)),
    ...passend.filter((eintrag) => eintrag.vorbei).sort((a, b) => b.startAt.localeCompare(a.startAt)),
  ];
  const seiten = Math.max(1, Math.ceil(sortiert.length / PRO_SEITE));
  const aktuelleSeite = Math.min(seite, seiten);
  const sichtbar = sortiert.slice((aktuelleSeite - 1) * PRO_SEITE, aktuelleSeite * PRO_SEITE);
  const zaehle = (welche: 'TRAINING' | 'WORK' | 'MATCH') => eintraege.filter((eintrag) => eintrag.art === welche
    && (zeit === 'alle' || (zeit === 'kommend') !== eintrag.vorbei)).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Select aria-label="Terminart" value={art2} onChange={(e) => { setArt2(e.target.value as typeof art2); setSeite(1); }} className="w-auto">
            <option value="alle">Alle Termine ({sortiert.length})</option>
            <option value="TRAINING">Trainings ({zaehle('TRAINING')})</option>
            <option value="MATCH">Spiele ({zaehle('MATCH')})</option>
            <option value="WORK">Arbeitseinsätze ({zaehle('WORK')})</option>
          </Select>
          <Select aria-label="Zeitraum" value={zeit} onChange={(e) => { setZeit(e.target.value as typeof zeit); setSeite(1); }} className="w-auto">
            <option value="kommend">Kommende</option>
            <option value="vergangen">Vergangene</option>
            <option value="alle">Alle</option>
          </Select>
          {art2 === 'MATCH' && (
            <Select aria-label="Heim oder Auswärts" value={spielOrt} onChange={(e) => { onSpielOrt(e.target.value as typeof spielOrt); setSeite(1); }} className="w-auto">
              <option value="alle">Heim und Auswärts</option>
              <option value="heim">Nur Heim</option>
              <option value="auswaerts">Nur Auswärts</option>
            </Select>
          )}
        </div>
        <div className="flex gap-2">
          {mayCreateMatch && <Button variant="secondary" onClick={onCreateMatch}>Spiel anlegen</Button>}
          {mayTrain && <Button onClick={() => setDialog(true)}>Termin anlegen</Button>}
        </div>
      </div>

      {isLoading && <LoadingBlock />}

      {/* Ein Ladefehler ist kein leerer Zustand: Meldung des Servers und ein Weg zurueck. */}
      {ladeFehler && (
        <div className="space-y-2" role="alert">
          <ErrorBlock error={ladeFehler.error} />
          <Button size="sm" variant="secondary" onClick={ladeFehler.erneut}>Erneut laden</Button>
        </div>
      )}

      <Card>
        <CardBody>
          {sichtbar.length ? (
            <ul className="space-y-2">{sichtbar.map((eintrag) => eintrag.zeile)}</ul>
          ) : isLoading || ladeFehler ? null : (
            <EmptyState
              title="Kein Termin gefunden"
              description={mayTrain
                ? 'Lege einen Einzeltermin, eine Serie oder ein Spiel an - oder ändere den Filter.'
                : 'Für diese Mannschaft steht in dieser Auswahl nichts an.'}
            />
          )}
          {seiten > 1 && (
            <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-200 pt-3">
              <Button size="sm" variant="secondary" disabled={aktuelleSeite <= 1} onClick={() => setSeite(aktuelleSeite - 1)}>
                Zurück
              </Button>
              <span className="text-sm text-slate-600">Seite {aktuelleSeite} von {seiten}</span>
              <Button size="sm" variant="secondary" disabled={aktuelleSeite >= seiten} onClick={() => setSeite(aktuelleSeite + 1)}>
                Weiter
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={dialog}
        onClose={() => setDialog(false)}
        title={art === 'WORK' ? 'Arbeitseinsatz anlegen' : 'Training anlegen'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog(false)}>
              Abbrechen
            </Button>
            <Button
              loading={createSession.isPending || createSeries.isPending}
              disabled={!title.trim() || (serie && tage.length === 0)}
              onClick={() => (serie ? createSeries : createSession).mutate()}
            >
              {serie ? 'Serie anlegen' : 'Anlegen'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Art">
            {(id) => (
              <Select
                id={id}
                value={art}
                onChange={(e) => {
                  const gewaehlt = e.target.value as 'TRAINING' | 'WORK';
                  setArt(gewaehlt);
                  // Der Titel folgt der Art, solange ihn niemand angefasst hat.
                  setTitle((vorher) => (vorher === 'Training' || vorher === 'Arbeitseinsatz'
                    ? (gewaehlt === 'WORK' ? 'Arbeitseinsatz' : 'Training') : vorher));
                }}
              >
                <option value="TRAINING">Training</option>
                <option value="WORK">Arbeitseinsatz</option>
              </Select>
            )}
          </Field>
          <Field label="Titel" required>
            {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
          </Field>

          <div className="flex gap-2 rounded-lg bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setSerie(false)}
              className={cn(
                'flex-1 rounded-md px-3 py-1.5 text-sm font-medium',
                serie ? 'text-slate-600' : 'bg-white shadow-sm',
              )}
            >
              Einzeltermin
            </button>
            <button
              type="button"
              onClick={() => setSerie(true)}
              className={cn(
                'flex-1 rounded-md px-3 py-1.5 text-sm font-medium',
                serie ? 'bg-white shadow-sm' : 'text-slate-600',
              )}
            >
              Wiederkehrend
            </button>
          </div>

          {!serie && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Beginn" required>
                {(id) => (
                  <Input
                    id={id}
                    type="datetime-local"
                    value={start}
                    onChange={(e) => {
                      setStart(e.target.value);
                      // Das Ende wandert mit, solange es sonst vor dem Beginn
                      // laege - sonst weist der Server erst beim Speichern ab.
                      if (e.target.value && e.target.value >= ende) {
                        setEnde(plusMinutes(e.target.value, 90));
                      }
                    }}
                  />
                )}
              </Field>
              <Field label="Ende" required>
                {(id) => (
                  <Input
                    id={id}
                    type="datetime-local"
                    value={ende}
                    onChange={(e) => setEnde(e.target.value)}
                  />
                )}
              </Field>
            </div>
          )}

          {serie && (
            <>
              <Field label="Wochentage" required hint="Mehrfachauswahl">
                {() => (
                  <div className="flex flex-wrap gap-1">
                    {([['Mo', 1], ['Di', 2], ['Mi', 3], ['Do', 4], ['Fr', 5], ['Sa', 6], ['So', 0]] as const).map(
                      ([label, tag]) => {
                        const gewaehlt = tage.includes(tag);
                        return (
                          <button
                            key={label}
                            type="button"
                            aria-pressed={gewaehlt}
                            onClick={() =>
                              setTage(gewaehlt ? tage.filter((t) => t !== tag) : [...tage, tag])
                            }
                            className={cn(
                              'h-9 w-11 rounded-lg border text-sm font-medium',
                              gewaehlt
                                ? 'border-brand-600 bg-brand-600 text-white'
                                : 'border-slate-300 bg-white text-slate-700',
                            )}
                          >
                            {label}
                          </button>
                        );
                      },
                    )}
                  </div>
                )}
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Von" required>
                  {(id) => (
                    <Input id={id} type="date" value={von} onChange={(e) => setVon(e.target.value)} />
                  )}
                </Field>
                <Field label="Bis" required>
                  {(id) => (
                    <Input id={id} type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
                  )}
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Beginn" required>
                  {(id) => (
                    <Input
                      id={id}
                      type="time"
                      value={vonZeit}
                      onChange={(e) => setVonZeit(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Ende" required>
                  {(id) => (
                    <Input
                      id={id}
                      type="time"
                      value={bisZeit}
                      onChange={(e) => setBisZeit(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </>
          )}

          <Field label="Ort">
            {(id) => (
              <Input
                id={id}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Sportplatz"
              />
            )}
          </Field>

          {einladbar.length > 0 && (
            <Field label="Gemeinsam mit" hint="Der Kader wird zu jedem Termin eingeladen und bleibt aktuell">
              {() => (
                <div className="flex flex-wrap gap-3">
                  {einladbar.map((t) => (
                    <Checkbox
                      key={t.id}
                      label={t.name}
                      checked={gaeste.includes(t.id)}
                      onChange={(an) => setGaeste(an ? [...gaeste, t.id] : gaeste.filter((id) => id !== t.id))}
                    />
                  ))}
                </div>
              )}
            </Field>
          )}
        </div>
      </Dialog>

      <Dialog
        open={absage !== null}
        onClose={() => setAbsage(null)}
        title={`${bezeichnung(absage)} absagen`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAbsage(null)}>
              Abbrechen
            </Button>
            <Button
              loading={absagen.isPending}
              onClick={() => absage && absagen.mutate({ session: absage, grund })}
            >
              {bezeichnung(absage)} absagen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            {absage?.title.replace(/^Abgesagt: /, '')} am{' '}
            {absage ? formatShort(absage.start_at) : ''} entfällt für die ganze Mannschaft. Zu- und
            Absagen sind dann nicht mehr möglich, und der Termin zählt in keiner Auswertung mit.
            Die Absage lässt sich wieder aufheben.
          </p>
          <Field label="Grund" hint="Steht beim Termin und ist für die Mannschaft sichtbar">
            {(id) => (
              <Input
                id={id}
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                placeholder="z. B. Platz gesperrt"
                autoFocus
              />
            )}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={loeschen !== null}
        onClose={() => setLoeschen(null)}
        title={`${bezeichnung(loeschen)} löschen`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLoeschen(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              loading={loeschMutation.isPending}
              disabled={!loeschVorschau.data || loeschVorschau.data.deleted === 0}
              onClick={() => loeschen && loeschMutation.mutate({ id: loeschen.id, scope: loeschUmfang })}
            >
              {loeschVorschau.data && loeschVorschau.data.deleted > 1
                ? `${loeschVorschau.data.deleted} ${loeschen?.kind === 'WORK' ? 'Arbeitseinsätze' : 'Trainings'} löschen`
                : `${bezeichnung(loeschen)} löschen`}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            {loeschen?.title.replace(/^Abgesagt: /, '')} am {loeschen ? formatShort(loeschen.start_at) : ''}.
            Gelöschte Termine verschwinden samt Rückmeldungen aus Kalender und Übersichten. Fällt ein
            {bezeichnung(loeschen)} nur aus, ist <strong>{bezeichnung(loeschen)} absagen</strong> besser – das bleibt sichtbar.
          </p>
          <div className="space-y-2">
            {([
              ['single', `Nur ${loeschen?.kind === 'WORK' ? 'dieser' : 'dieses'} ${bezeichnung(loeschen)}`, 'Die übrigen Termine bleiben'],
              ['following', `${loeschen?.kind === 'WORK' ? 'Dieser' : 'Dieses'} und alle folgenden der Serie`, 'Frühere Termine der Serie bleiben'],
            ] as const).map(([value, label, hint]) => (
              <label
                key={value}
                className={cn(
                  'flex cursor-pointer gap-2 rounded-lg border px-3 py-2.5 text-sm',
                  loeschUmfang === value ? 'border-rose-400 bg-rose-50' : 'border-slate-200 hover:border-slate-300',
                )}
              >
                <input
                  type="radio"
                  name="loeschUmfang"
                  className="mt-0.5 h-4 w-4 accent-rose-600"
                  checked={loeschUmfang === value}
                  onChange={() => setLoeschUmfang(value)}
                />
                <span>
                  <span className="block font-medium text-slate-800">{label}</span>
                  <span className="block text-xs text-slate-500">{hint}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="text-sm text-slate-600" aria-live="polite">
            {loeschVorschau.isLoading && 'Wird gezählt …'}
            {loeschVorschau.error && (loeschVorschau.error instanceof ApiError ? loeschVorschau.error.message : 'Vorschau nicht möglich')}
            {loeschVorschau.data &&
              `${loeschVorschau.data.deleted} ${loeschVorschau.data.deleted === 1 ? 'Training wird' : 'Trainings werden'} gelöscht.`}
            {loeschVorschau.data && loeschVorschau.data.skipped > 0 &&
              ` ${loeschVorschau.data.skipped} mit erfasster Anwesenheit ${loeschVorschau.data.skipped === 1 ? 'bleibt' : 'bleiben'} stehen.`}
          </p>
        </div>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------- Einladungslink */

function ladeNeueLinks(key: string): GeneratedPersonalAccessLink[] {
  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as GeneratedPersonalAccessLink[]) : [];
  } catch {
    return [];
  }
}

function speichereNeueLinks(key: string, value: GeneratedPersonalAccessLink[]): void {
  try {
    if (value.length === 0) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ohne Speicher gehen die Links beim Reiterwechsel verloren - wie bisher.
  }
}

/**
 * Link zur Zu- und Absage ohne Anmeldung.
 *
 * Spieler melden sich nicht an, um zuzusagen. Jeder Link gehoert genau einer
 * Person und zeigt weder den Kader noch Rueckmeldungen anderer Personen.
 */
function EinladungCard({ teamId, seasonId }: { teamId: string; seasonId: string }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  // Frisch erzeugte Links werden nur einmal geliefert: Über den Reiterwechsel
  // in der Sitzung aufbewahren, bis sie bewusst ausgeblendet werden.
  const neuKey = `vereinsplaner:neue-links:${teamId}:${seasonId}`;
  const [neu, setNeuState] = useState<GeneratedPersonalAccessLink[]>(() => ladeNeueLinks(neuKey));
  const setNeu = (value: GeneratedPersonalAccessLink[]): void => {
    setNeuState(value);
    speichereNeueLinks(neuKey, value);
  };
  const [einzeln, setEinzeln] = useState<GeneratedPersonalAccessLink | null>(null);
  const [sortierung, setSortierung] = useState<'name' | 'zuletzt' | 'nie'>('name');

  const queryKey = ['sports-invite', teamId, seasonId];
  const links = useQuery({
    queryKey,
    queryFn: () => api.get<PersonalAccessLink[]>(`/api/sports/teams/${teamId}/access-links`, { seasonId }),
    enabled: Boolean(teamId && seasonId),
  });

  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const erzeugen = useMutation({
    mutationFn: (personIds?: string[]) => api.post<GeneratedPersonalAccessLink[]>(`/api/sports/teams/${teamId}/access-links?seasonId=${seasonId}`, personIds ? { personIds } : {}),
    onSuccess: async (result) => {
      setNeu(result);
      // Ein einzelner Link: gleich zum Verschicken anbieten.
      if (result.length === 1) setEinzeln(result[0]!);
      await qc.invalidateQueries({ queryKey });
      toast.show(result.length === 1 ? 'Persönlicher Link erzeugt' : `${result.length} persönliche Links erzeugt`);
    },
    onError: fehler('Persönliche Links konnten nicht erzeugt werden'),
  });

  const widerrufen = useMutation({
    mutationFn: (id: string) => api.del(`/api/sports/access-links/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
      toast.show('Link widerrufen');
    },
    onError: fehler('Link konnte nicht widerrufen werden'),
  });

  const freigeben = useMutation({
    mutationFn: (claimId: string) => api.del(`/api/sports/registration-claims/${claimId}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
      toast.show('Anmeldung zurückgesetzt – der Name ist wieder wählbar');
    },
    onError: fehler('Anmeldung konnte nicht zurückgesetzt werden'),
  });

  const adresse = (token: string) => `${window.location.origin}/antworten#${token}`;

  const alleErzeugen = async (): Promise<void> => {
    const betroffen = (links.data ?? []).filter((l) => !l.claim_id);
    const aktiv = betroffen.filter((l) => l.link_id).length;
    const ok = await confirm({
      title: 'Links für den gesamten Kader erzeugen?',
      message: `Für ${betroffen.length} ${betroffen.length === 1 ? 'Person wird' : 'Personen werden'} neue Links erzeugt. ${
        aktiv > 0
          ? `${aktiv} ${aktiv === 1 ? 'Person hat' : 'Personen haben'} bereits einen aktiven Link: Bereits verteilte Links dieser Personen werden ungültig – sie müssen den neuen Link bekommen.`
          : 'Bisher hat niemand davon einen aktiven Link.'
      }`,
      confirmLabel: 'Ja, Links erzeugen',
      tone: aktiv > 0 ? 'danger' : 'default',
    });
    if (ok) erzeugen.mutate(undefined);
  };
  const einzelnErzeugen = async (link: PersonalAccessLink): Promise<void> => {
    const name = `${link.first_name} ${link.last_name}`;
    if (link.link_id) {
      const selbst = link.claim_id || link.claim_email;
      const ok = await confirm({
        title: `Neuen Link für ${name} erzeugen?`,
        message: `Der bisherige Link von ${name} wird ungültig.${selbst ? ` ${name} hat sich selbst angemeldet und bekommt den Link sonst per Mail.` : ''}`,
        confirmLabel: 'Ja, neu erzeugen',
        tone: 'danger',
      });
      if (!ok) return;
    }
    erzeugen.mutate([link.person_id]);
  };
  const linkWiderrufen = async (link: PersonalAccessLink): Promise<void> => {
    const name = `${link.first_name} ${link.last_name}`;
    const ok = await confirm({
      title: `Link von ${name} widerrufen?`,
      message: `Der Link von ${name} funktioniert danach nicht mehr. Wer ihn schon bekommen hat, kann damit nicht mehr zu- oder absagen.`,
      confirmLabel: 'Ja, widerrufen',
      tone: 'danger',
    });
    if (ok && link.link_id) widerrufen.mutate(link.link_id);
  };
  const anmeldungZuruecksetzen = async (link: PersonalAccessLink): Promise<void> => {
    const name = `${link.first_name} ${link.last_name}`;
    const ok = await confirm({
      title: `Anmeldung von ${name} zurücksetzen?`,
      message: `${name} ist danach nicht mehr mit ${link.claim_email ?? 'der E-Mail-Adresse'} verknüpft. Der Name ist im Anmeldelink wieder wählbar.`,
      confirmLabel: 'Ja, zurücksetzen',
      tone: 'danger',
    });
    if (ok && link.claim_id) freigeben.mutate(link.claim_id);
  };
  const ziel = (link: GeneratedPersonalAccessLink): ShareTarget => ({
    name: link.name, firstName: link.firstName, url: adresse(link.token), phone: link.phone, email: link.email,
  });
  const zuletzt = (link: PersonalAccessLink): number => (link.last_seen_at ? Date.parse(link.last_seen_at) : 0);
  const sortiert = [...(links.data ?? [])].sort((a, b) => {
    if (sortierung === 'zuletzt') return zuletzt(b) - zuletzt(a);
    // Noch nie geoeffnet, aber mit Link - wer da haengt, braucht Hilfe.
    if (sortierung === 'nie') return Number(Boolean(a.last_seen_at) || !a.link_id) - Number(Boolean(b.last_seen_at) || !b.link_id);
    return 0;
  });
  const mitLink = (links.data ?? []).filter((l) => l.link_id);
  const geoeffnet = mitLink.filter((l) => l.last_seen_at);

  // Zuerst die Personen: "Link an Spieler X schicken" ist der haeufigste Fall.
  // Was fuer alle auf einmal gilt, steht darunter; Erklaerungen klappen auf.
  return (
    <Card>
      <CardBody className="space-y-3">
        <SectionTitle
          title="Persönliche Links zur Rückmeldung"
          subtitle="Ohne Registrierung – jeder Link gehört genau einer Person"
        />
        <ShareDialog target={einzeln ? ziel(einzeln) : null} onClose={() => setEinzeln(null)} />
        {confirmDialog}

        {links.isLoading && <LoadingBlock />}
        {!links.isLoading && links.data?.length === 0 && <EmptyState title="Kein Kader eingetragen" description="Persönliche Links können nach dem Eintragen des Kaders erzeugt werden." />}

        {neu.length > 0 && (
          <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-amber-900">Jetzt verteilen – diese Links werden nur einmal angezeigt.</p>
            {neu.map((link) => (
              <div key={link.id} className="grid gap-2 rounded-md bg-white p-2 sm:grid-cols-[minmax(8rem,1fr)_minmax(0,2fr)_auto] sm:items-center">
                <span className="text-sm font-medium">{link.name}</span>
                <Input readOnly value={adresse(link.token)} aria-label={`Link für ${link.name}`} />
                <ShareActions target={ziel(link)} compact />
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setNeu([])}>Ausblenden</Button>
          </div>
        )}

        {links.data && links.data.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-600">
              Geöffnet <strong>{geoeffnet.length} von {mitLink.length}</strong>
              {mitLink.length > geoeffnet.length && <span className="text-slate-500"> ({mitLink.length - geoeffnet.length} noch nie)</span>}
              {' · '}Push <strong>{links.data.filter((l) => l.push_link + l.push_account > 0).length} von {links.data.length}</strong>
            </p>
            <Select aria-label="Sortierung" value={sortierung} onChange={(e) => setSortierung(e.target.value as typeof sortierung)} className="w-auto">
              <option value="name">Nach Name</option>
              <option value="zuletzt">Zuletzt geöffnet zuerst</option>
              <option value="nie">Noch nie geöffnet zuerst</option>
            </Select>
          </div>
        )}

        {links.data && links.data.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {sortiert.map((link) => (
              <li key={link.person_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                    {link.first_name} {link.last_name}
                    {link.push_link + link.push_account > 0 && (
                      <Badge tone="success" title={link.push_link > 0 ? 'Push über den persönlichen Link' : 'Push über den eigenen Zugang'}>Push</Badge>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {link.link_id ? `Aktiv bis ${formatShort(link.expires_at!)}` : 'Noch kein aktiver Link'}
                    {link.link_id && (link.last_seen_at
                      ? <span title={formatShort(link.last_seen_at)}> · zuletzt geöffnet {relativeDays(link.last_seen_at)}</span>
                      : <span className="font-medium text-amber-700"> · noch nie geöffnet</span>)}
                  </p>
                  {link.claim_email && (
                    <p className="text-xs text-emerald-700">Selbst angemeldet mit {link.claim_email}{link.claimed_at ? ` am ${formatShort(link.claimed_at)}` : ''}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Links liegen nur verschluesselt vor: Teilen heisst neu erzeugen,
                      der alte Link wird dabei ungueltig. */}
                  <Button
                    size="sm"
                    variant={link.link_id ? 'secondary' : 'primary'}
                    loading={erzeugen.isPending}
                    title={link.link_id ? 'Erzeugt einen neuen Link - der bisherige wird ungültig' : undefined}
                    onClick={() => void einzelnErzeugen(link)}
                  >
                    {link.link_id ? 'Neu teilen' : 'Link teilen'}
                  </Button>
                  <MehrMenu
                    label={`Weitere Aktionen · ${link.first_name} ${link.last_name}`}
                    eintraege={[
                      ...(link.claim_id ? [{ label: 'Anmeldung zurücksetzen', onSelect: () => void anmeldungZuruecksetzen(link) }] : []),
                      ...(link.link_id ? [{ label: 'Widerrufen', gefaehrlich: true, onSelect: () => void linkWiderrufen(link) }] : []),
                    ]}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        {Boolean(links.data?.length) && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-semibold text-slate-800">Für alle auf einmal</p>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" loading={erzeugen.isPending} onClick={() => void alleErzeugen()}>Links für gesamten Kader erzeugen</Button>
              <span className="text-xs text-slate-500">Selbst angemeldete Personen werden ausgelassen.</span>
            </div>
            <RegistrationLinkCard teamId={teamId} seasonId={seasonId} />
          </div>
        )}

        <details className="text-sm text-slate-600">
          <summary className="cursor-pointer font-medium text-brand-700">Wie funktionieren die Links?</summary>
          <p className="mt-2">
            Die Person muss kein Konto anlegen. Der Link zeigt ausschließlich ihre eigenen Termine und Rückmeldungen.
            Beim Erzeugen wird ein vorhandener Link dieser Person ungültig – deshalb heißt der Knopf „Neu teilen“.
            Mit dem Anmeldelink für die Gruppe wählen Spieler ihren Namen selbst und bekommen den Link per E-Mail.
          </p>
        </details>
      </CardBody>
    </Card>
  );
}

/* ----------------------------------------------------------------- Kader */

/**
 * Geburtsdatum in der Kaderzeile: Jahrgang und Alter auf einen Blick, im
 * Nachwuchs mit Hinweis, wenn der Jahrgang nicht zur Altersklasse passt.
 * Gepflegt wird es an der Person - hier nur der kurze Weg dorthin.
 */
function Geburtsdatum({ row, team, season, editable }: { row: RosterRow; team: Team; season: Season | undefined; editable: boolean }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [bearbeiten, setBearbeiten] = useState(false);
  const [wert, setWert] = useState(row.birth_date ?? '');

  const speichern = useMutation({
    mutationFn: (birthDate: string | null) => api.patch(`/api/persons/${row.person_id}`, { birthDate }),
    onSuccess: async () => {
      setBearbeiten(false);
      await qc.invalidateQueries({ queryKey: ['sports-roster'] });
      toast.show('Geburtsdatum gespeichert');
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Geburtsdatum konnte nicht gespeichert werden', 'error'),
  });

  if (bearbeiten) {
    return (
      <span className="mt-1 flex flex-wrap items-center gap-2">
        <Input
          type="date"
          autoFocus
          aria-label={`Geburtsdatum ${row.first_name} ${row.last_name}`}
          max={today()}
          value={wert}
          className="h-8 w-auto rounded-lg py-0 text-sm"
          onChange={(event) => setWert(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Escape') setBearbeiten(false); }}
        />
        <Button size="sm" loading={speichern.isPending} onClick={() => speichern.mutate(wert || null)}>Speichern</Button>
        <Button size="sm" variant="ghost" onClick={() => setBearbeiten(false)}>Abbrechen</Button>
      </span>
    );
  }

  const jugend = team.category === 'YOUTH' && team.ageClass;
  const klasse = row.birth_date && season ? ageClassFor(row.birth_date, season.start_date) : null;
  const hinweis = jugend && klasse !== null
    ? klasse > (team.ageClass as number)
      ? { text: `älter als U${team.ageClass} – Spielberechtigung prüfen`, ton: 'text-amber-700' }
      : klasse < (team.ageClass as number)
        ? { text: `Jahrgang U${klasse}, spielt hoch`, ton: 'text-slate-500' }
        : null
    : null;

  return (
    <span className="block text-xs">
      {row.birth_date ? (
        <span className="text-slate-500" title={formatDay(row.birth_date)}>
          Jg. {row.birth_date.slice(0, 4)} · {ageOn(row.birth_date, today())} J.
        </span>
      ) : (
        <span className={jugend ? 'text-amber-700' : 'text-slate-400'}>Geburtsdatum fehlt</span>
      )}
      {hinweis && <span className={cn('ml-1', hinweis.ton)}>· {hinweis.text}</span>}
      {editable && (
        <button
          type="button"
          className="ml-2 text-brand-700 hover:underline"
          onClick={() => { setWert(row.birth_date ?? ''); setBearbeiten(true); }}
        >
          {row.birth_date ? 'ändern' : 'eintragen'}
        </button>
      )}
    </span>
  );
}

function KaderTab({
  teamId,
  seasonId,
  rows,
  team,
  season,
  otherTeams,
}: {
  team: Team;
  season: Season | undefined;
  otherTeams: Team[];
  teamId: string;
  seasonId: string;
  rows: RosterRow[] | undefined;
}): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const qc = useQueryClient();
  const [personId, setPersonId] = useState('');
  const [nummer, setNummer] = useState('');
  const [position, setPosition] = useState('');
  const [status, setStatus] = useState<RosterStatus>('ACTIVE');
  const [tags, setTags] = useState<string[]>([]);
  const [formOffen, setFormOffen] = useState(false);
  const [aushilfenZeigen, setAushilfenZeigen] = useState(false);
  const [wechsel, setWechsel] = useState<RosterRow | null>(null);
  const [tagFilter, setTagFilter] = useState('');
  const [tagsBearbeiten, setTagsBearbeiten] = useState<RosterRow | null>(null);

  const mayRoster = can('persons', 'update');
  // Tags pflegt, wer die Mannschaft betreut - dafuer braucht es nicht das
  // Recht, Personendaten zu bearbeiten.
  const mayTags = can('sports_participation', 'update');

  // Vorschlaege aus dem ganzen Verein - gemeinsames Vokabular statt vieler Schreibweisen.
  const tagVorschlaege = useQuery({
    queryKey: ['sports-roster-tags', teamId],
    queryFn: () => api.get<{ tag: string; count: number }[]>(`/api/sports/teams/${teamId}/roster-tags`),
    enabled: Boolean(teamId),
  });
  const vorschlaege = (tagVorschlaege.data ?? []).map((entry) => entry.tag);

  const personen = useQuery({
    queryKey: ['sports-persons'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/api/sports/persons'),
    enabled: mayRoster,
  });
  // Die Kaderauswahl kennt nur Personen aus dem eigenen Zugriffsbereich -
  // deshalb eine eigene Liste statt der allgemeinen Personenauswahl.
  const auswahl = useMemo(
    () => (personen.data ?? []).map((p) => ({ id: p.id, displayName: p.name })),
    [personen.data],
  );

  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const speichern = useMutation({
    mutationFn: () =>
      api.put(`/api/sports/teams/${teamId}/roster?seasonId=${seasonId}`, {
        personId,
        jerseyNumber: nummer ? Number(nummer) : null,
        position,
        status,
        tags,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sports-roster'] });
      await qc.invalidateQueries({ queryKey: ['sports-teams'] });
      await qc.invalidateQueries({ queryKey: ['sports-roster-tags'] });
      setPersonId('');
      setNummer('');
      setPosition('');
      setStatus('ACTIVE');
      setTags([]);
      setFormOffen(false);
      toast.show('Kader gespeichert');
    },
    onError: fehler('Kader konnte nicht gespeichert werden'),
  });

  const statusAendern = useMutation({
    mutationFn: (input: { row: RosterRow; status: RosterStatus }) =>
      api.put(`/api/sports/teams/${teamId}/roster?seasonId=${seasonId}`, {
        personId: input.row.person_id,
        jerseyNumber: input.row.jersey_number,
        position: input.row.position,
        status: input.status,
      }),
    onSuccess: async (_data, input) => {
      await qc.invalidateQueries({ queryKey: ['sports-roster'] });
      await qc.invalidateQueries({ queryKey: ['sports-teams'] });
      await qc.invalidateQueries({ queryKey: ['sports-summary'] });
      toast.show(input.status === 'OCCASIONAL' ? 'Als Aushilfe / inaktiv markiert' : 'In den Stammkader übernommen');
    },
    onError: fehler('Status konnte nicht geändert werden'),
  });

  const tagsSpeichern = useMutation({
    mutationFn: (input: { row: RosterRow; tags: string[] }) =>
      api.put(`/api/sports/teams/${teamId}/roster/${input.row.person_id}/tags?seasonId=${seasonId}`, {
        tags: input.tags,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sports-roster'] });
      await qc.invalidateQueries({ queryKey: ['sports-roster-tags'] });
      await qc.invalidateQueries({ queryKey: ['sports-participants'] });
      setTagsBearbeiten(null);
      toast.show('Tags gespeichert');
    },
    onError: fehler('Tags konnten nicht gespeichert werden'),
  });

  // Filter nach Tag: hilft bei "wer ist im Internat?" oder "wer hat Schicht?".
  const passtZumFilter = (row: RosterRow): boolean =>
    !tagFilter || row.tags.some((tag) => tag === tagFilter);
  const vorhandeneTags = [...new Set((rows ?? []).flatMap((row) => row.tags))].sort((x, y) => x.localeCompare(y, 'de'));
  const stamm = (rows ?? []).filter((row) => row.status !== 'OCCASIONAL').filter(passtZumFilter);
  const aushilfen = (rows ?? []).filter((row) => row.status === 'OCCASIONAL').filter(passtZumFilter);

  const kaderZeile = (row: RosterRow): JSX.Element => (
    <li key={row.person_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
      <div className="min-w-0">
        <Link
          to={`/personen/${row.person_id}`}
          className="text-slate-900 hover:text-brand-700 hover:underline"
        >
          {row.first_name} {row.last_name}
          <span className="text-slate-500">
            {row.position ? ` · ${row.position}` : ''}
            {row.jersey_number !== null ? ` · Nr. ${row.jersey_number}` : ''}
          </span>
        </Link>
        <Geburtsdatum row={row} team={team} season={season} editable={mayRoster} />
        {tagsBearbeiten?.person_id === row.person_id ? (
          <div className="mt-1 max-w-md">
            <TagPicker
              value={tagsBearbeiten.tags}
              suggestions={vorschlaege}
              onChange={(next) => setTagsBearbeiten({ ...tagsBearbeiten, tags: next })}
            />
            <div className="mt-1 flex gap-2">
              <Button size="sm" loading={tagsSpeichern.isPending} onClick={() => tagsSpeichern.mutate({ row, tags: tagsBearbeiten.tags })}>
                Speichern
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setTagsBearbeiten(null)}>Abbrechen</Button>
            </div>
          </div>
        ) : (
          <span className="mt-1 flex flex-wrap items-center gap-1">
            <TagList tags={row.tags} />
            {mayTags && (
              <button
                type="button"
                className="text-xs text-brand-700 hover:underline"
                onClick={() => setTagsBearbeiten(row)}
              >
                {row.tags.length > 0 ? 'Tags ändern' : '+ Tags'}
              </button>
            )}
          </span>
        )}
      </div>
      {mayRoster && (
        <MehrMenu
          label={`Weitere Aktionen · ${row.first_name} ${row.last_name}`}
          eintraege={[
            {
              label: row.status === 'OCCASIONAL' ? 'In Stammkader' : 'Als Aushilfe',
              onSelect: () => statusAendern.mutate({ row, status: row.status === 'OCCASIONAL' ? 'ACTIVE' : 'OCCASIONAL' }),
            },
            ...(otherTeams.length > 0 ? [{ label: 'Wechseln', onSelect: () => setWechsel(row) }] : []),
            { label: 'Entfernen', gefaehrlich: true, onSelect: () => void rowEntfernen(row) },
          ]}
        />
      )}
    </li>
  );

  const entfernen = useMutation({
    mutationFn: (id: string) =>
      api.del(`/api/sports/teams/${teamId}/roster/${id}?seasonId=${seasonId}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sports-roster'] });
      await qc.invalidateQueries({ queryKey: ['sports-teams'] });
      toast.show('Person aus dem Kader entfernt');
    },
    onError: fehler('Kader konnte nicht geändert werden'),
  });

  const rowEntfernen = async (row: RosterRow): Promise<void> => {
    const name = `${row.first_name} ${row.last_name}`;
    const ok = await confirm({
      title: `${name} aus dem Kader entfernen?`,
      message: `Rückennummer und Position gehen verloren, künftige Termine verliert ${name}.`,
      confirmLabel: 'Ja, entfernen',
      tone: 'danger',
    });
    if (ok) entfernen.mutate(row.person_id);
  };

  return (
    <Card>
      <CardBody>
        {confirmDialog}
        <SectionTitle
          title="Kader"
          subtitle="Gilt für die gewählte Saison"
          action={mayRoster && !formOffen ? <Button size="sm" onClick={() => setFormOffen(true)}>Person hinzufügen</Button> : undefined}
        />
        {mayRoster && formOffen && (
          <div className="mb-4 grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-[2fr_1fr_1fr_1fr_auto]">
            <PersonSelect
              value={personId || null}
              onChange={(v) => setPersonId(v ?? '')}
              options={auswahl}
              ariaLabel="Person für Kader"
              placeholder="Person suchen …"
              emptyText="Keine Person im Zugriff."
            />
            <Input
              aria-label="Rückennummer"
              type="number"
              min="0"
              value={nummer}
              onChange={(e) => setNummer(e.target.value)}
              placeholder="Nr."
            />
            <Input
              aria-label="Position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Position"
            />
            <Select
              aria-label="Kaderstatus"
              value={status}
              onChange={(e) => setStatus(e.target.value as RosterStatus)}
            >
              {ROSTER_STATUS.map((wert) => (
                <option key={wert} value={wert}>
                  {ROSTER_STATUS_LABELS[wert]}
                </option>
              ))}
            </Select>
            <Button
              disabled={!personId || !seasonId}
              loading={speichern.isPending}
              onClick={() => speichern.mutate()}
            >
              Speichern
            </Button>
            <div className="md:col-span-5">
              <p className="label">Tags (optional)</p>
              <p className="hint mb-1">Zusatzinfos wie „U16“, „Internat“ oder „Schichtarbeit“.</p>
              <TagPicker value={tags} suggestions={vorschlaege} onChange={setTags} />
            </div>
            <div className="md:col-span-5">
              <Button size="sm" variant="ghost" onClick={() => setFormOffen(false)}>Abbrechen</Button>
            </div>
          </div>
        )}

        {vorhandeneTags.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Filter:</span>
            {vorhandeneTags.map((tag) => (
              <button
                key={tag}
                type="button"
                aria-pressed={tagFilter === tag}
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-medium',
                  tagFilter === tag ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                )}
                onClick={() => setTagFilter(tagFilter === tag ? '' : tag)}
              >
                {tag}
              </button>
            ))}
            {tagFilter && (
              <button type="button" className="text-xs text-brand-700 hover:underline" onClick={() => setTagFilter('')}>
                Filter aufheben
              </button>
            )}
          </div>
        )}

        {rows?.length ? (
          <>
            {stamm.length > 0 ? (
              <ul className="divide-y divide-slate-100">{stamm.map(kaderZeile)}</ul>
            ) : (
              <p className="text-sm text-slate-500">Kein Stammkader eingetragen.</p>
            )}
            {aushilfen.length > 0 && (
              <div className="mt-4 border-t border-slate-200 pt-3">
                <Checkbox
                  label={`Aushilfen / Inaktive anzeigen (${aushilfen.length})`}
                  checked={aushilfenZeigen}
                  onChange={setAushilfenZeigen}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Können zu- und absagen, erhalten aber keine Erinnerungen und sind in Übersichten
                  und Auswertungen standardmäßig ausgeblendet.
                </p>
                {aushilfenZeigen && (
                  <ul className="mt-2 divide-y divide-slate-100">{aushilfen.map(kaderZeile)}</ul>
                )}
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title="Kader leer"
            description="Füge dieser Mannschaft Personen hinzu."
          />
        )}
        <TransferHistory teamId={teamId} seasonId={seasonId} />
        {wechsel && (
          <TransferDialog row={wechsel} team={team} teams={otherTeams} seasonId={seasonId} onClose={() => setWechsel(null)} />
        )}
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------- Auswertungen */

/** Ein gewerteter Termin ist einer, keine Termine. */
function termine(anzahl: number): string {
  return `${anzahl} ${anzahl === 1 ? 'gewerteter Termin' : 'gewertete Termine'}`;
}

const KIND_LABELS: { id: string; label: string }[] = [
  { id: 'TRAINING', label: 'Trainings' },
  { id: 'MATCH', label: 'Spiele' },
  { id: 'ALLE', label: 'Alles' },
];

function AuswertungenTab({ teamId, seasonId, season }: { teamId: string; seasonId: string; season: Season | undefined }): JSX.Element {
  const [kind, setKind] = useState('TRAINING');
  const [aushilfenZeigen, setAushilfenZeigen] = useState(false);
  const [eigenerZeitraum, setEigenerZeitraum] = useState(false);
  const [von, setVon] = useState(() => season?.start_date ?? today(-90));
  const [bis, setBis] = useState(() => today());
  const zeitraumGueltig = Boolean(von && bis && bis >= von);
  const zeitraum = eigenerZeitraum && zeitraumGueltig ? { from: von, to: bis } : null;

  const summary = useQuery({
    queryKey: ['sports-summary', teamId, seasonId, kind, aushilfenZeigen, zeitraum?.from, zeitraum?.to],
    queryFn: () =>
      api.get<AttendanceSummary>(`/api/sports/teams/${teamId}/attendance-summary`, {
        seasonId,
        ...(kind === 'ALLE' ? {} : { kind }),
        ...(aushilfenZeigen ? { includeOccasional: true } : {}),
        ...(zeitraum ?? {}),
      }),
    enabled: Boolean(teamId && seasonId) && (!eigenerZeitraum || zeitraumGueltig),
    // Beim Aendern der Daten bleibt die bisherige Auswertung stehen, statt
    // bei jedem Tastendruck samt Filter zu verschwinden.
    placeholderData: (vorher) => vorher,
  });

  const zeitraumWahl = (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex gap-2 rounded-lg bg-slate-100 p-1">
          {[{ id: false, label: season ? `Saison ${season.name}` : 'Saison' }, { id: true, label: 'Eigener Zeitraum' }].map((eintrag) => (
            <button
              key={String(eintrag.id)}
              type="button"
              aria-pressed={eigenerZeitraum === eintrag.id}
              onClick={() => setEigenerZeitraum(eintrag.id)}
              className={cn('flex-1 rounded-md px-3 py-1.5 text-sm font-medium', eigenerZeitraum === eintrag.id ? 'bg-white shadow-sm' : 'text-slate-600')}
            >
              {eintrag.label}
            </button>
          ))}
        </div>
        {eigenerZeitraum && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Von">{(id) => <Input id={id} type="date" value={von} onChange={(e) => setVon(e.target.value)} />}</Field>
            <Field label="Bis" error={von && bis && bis < von ? 'Das Ende liegt vor dem Beginn' : undefined}>
              {(id) => <Input id={id} type="date" min={von} value={bis} onChange={(e) => setBis(e.target.value)} />}
            </Field>
          </div>
        )}
        {eigenerZeitraum && (
          <p className="text-xs text-slate-500">
            Zählt alle Termine der Mannschaft im Zeitraum, auch über Saisongrenzen hinweg. Kader und Rückennummern stammen aus der gewählten Saison.
          </p>
        )}
      </CardBody>
    </Card>
  );

  if (summary.isLoading) return <div className="space-y-3">{zeitraumWahl}<LoadingBlock /></div>;
  if (summary.error) {
    return <div className="space-y-3">{zeitraumWahl}<p className="text-sm text-rose-700">Die Auswertung konnte nicht geladen werden.</p></div>;
  }

  const daten = summary.data;
  if (!daten) return <div className="space-y-3">{zeitraumWahl}<EmptyState title="Keine Auswertung" description="Es liegen keine Daten vor." /></div>;

  return (
    <div className="space-y-3">
      {zeitraumWahl}
      <div className="flex gap-2 rounded-lg bg-slate-100 p-1">
        {KIND_LABELS.map((eintrag) => (
          <button
            key={eintrag.id}
            type="button"
            aria-pressed={kind === eintrag.id}
            onClick={() => setKind(eintrag.id)}
            className={cn(
              'flex-1 rounded-md px-3 py-1.5 text-sm font-medium',
              kind === eintrag.id ? 'bg-white shadow-sm' : 'text-slate-600',
            )}
          >
            {eintrag.label}
          </button>
        ))}
      </div>

      {daten.aushilfen > 0 && (
        <Checkbox
          label={`Aushilfen / Inaktive einbeziehen (${daten.aushilfen})`}
          checked={aushilfenZeigen}
          onChange={setAushilfenZeigen}
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kennzahl label="Teilnahmeanteil" wert={daten.gesamt.quote === null ? 'Noch keine Daten' : `${daten.gesamt.anwesend}/${daten.gesamt.erfasst} · ${daten.gesamt.quote} %`} />
        <Kennzahl label="Anwesend" wert={String(daten.gesamt.anwesend)} />
        <Kennzahl label="Abwesend" wert={String(daten.gesamt.abgemeldet)} />
        <Kennzahl label="Noch nicht abgeschlossen" wert={String(daten.termine.unbestaetigt)} />
      </div>

      <Card>
        <CardBody>
          <SectionTitle
            title="Je Person"
            subtitle={`${termine(daten.termine.gewertet)} · ${daten.termine.bestaetigt} bestätigt · ${daten.termine.kommend} kommend`}
          />

          {daten.spieler.length === 0 ? (
            <EmptyState
              title="Kein Kader"
              description="Für diese Mannschaft und Saison sind keine Personen eingetragen."
            />
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {daten.spieler.map((s) => (
                <li key={s.personId} className="rounded-lg border border-slate-200 p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span>
                      <Link to={`/personen/${s.personId}`} className="font-medium text-slate-800 hover:text-brand-700 hover:underline">{s.name}</Link>
                      {s.rosterStatus === 'OCCASIONAL' && !s.gastVon && <span className="ml-1 text-xs text-slate-500">· Aushilfe</span>}
                      {s.gastVon && <span className="ml-1.5 rounded bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700">von {s.gastVon}</span>}
                    </span>
                    {s.jerseyNumber !== null && <span className="text-xs text-slate-500">Nr. {s.jerseyNumber}</span>}
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-slate-600">
                    <span>Anwesend<br /><strong className="tabular-nums text-slate-900">{s.anwesend}</strong></span>
                    <span>Abwesend<br /><strong className="tabular-nums text-slate-900">{s.abgemeldet}</strong></span>
                    <span>Erfasst<br /><strong className="tabular-nums text-slate-900">{s.erfasst}</strong></span>
                  </div>
                  <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                    <span className={cn('rounded px-1.5 py-0.5 font-medium', ATTENDANCE_BAND_STYLE[s.band].feld)}>{ATTENDANCE_BAND_LABELS[s.band]}</span>
                    Teilnahmeanteil: {s.quote === null ? 'noch keine bestätigten Daten' : `${s.anwesend}/${s.erfasst} · ${s.quote} %`}
                  </p>
                  {s.entschuldigt > 0 && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      {s.entschuldigt} {s.entschuldigt === 1 ? 'Termin' : 'Termine'} entschuldigt – nicht gewertet
                    </p>
                  )}
                  {s.anderswo > 0 && (
                    <p className="mt-0.5 text-xs text-brand-700">+ {s.anderswo} {s.anderswo === 1 ? 'Einsatz' : 'Einsätze'} bei anderen Mannschaften</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {daten.termine.gewertet > 0 && daten.gesamt.erfasst === 0 && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Zu{' '}
              {daten.termine.gewertet === 1
                ? 'dem vergangenen Termin'
                : `den ${daten.termine.gewertet} vergangenen Terminen`}{' '}
              wurde noch keine Anwesenheit erfasst. Ohne geführte Liste gibt es nichts
              auszuwerten.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Kennzahl({
  label,
  wert,
}: {
  label: string;
  wert: string;
}): JSX.Element {
  return (
    <Card>
      <CardBody>
        <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums text-slate-900">
          {wert}
        </p>
      </CardBody>
    </Card>
  );
}
